/**
 * Factories Section Component
 * Smart component - wires up Redux actions to dumb components
 * Uses card-based layout consistent with gates and zones
 * Only accessible to super admins
 */

import { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import { useSelector, useDispatch, useTranslation, useIsRTL } from '@ufc/core'
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  Plus,
  Search,
  Building2,
  ShieldAlert,
} from '@ufc/uikit'

import {
  selectFactoriesItems,
  selectFactoriesStats,
  selectFactoriesLoading,
  selectFactoriesError,
  selectFactoriesFilter,
  selectLocations,
  selectCities,
  selectSelectedFactory,
  selectCitiesSearchResults,
  selectCitiesSearchLoading,
  selectCitiesHasMore,
  selectCitiesLoadMoreLoading,
} from '../../../store/factories.selectors'
import { factoriesActions } from '../../../store/factories.slice'
import type { FactoryListItem, Factory, CreateFactoryRequest, UpdateFactoryRequest } from '../../../domain/types'
import { FactoryCard } from '../../cards'
import type { FactoryFormSubmitData } from '../../drawers'
const FactoryFormDrawer = lazy(() => import('../../drawers/factory-form-drawer').then(m => ({ default: m.FactoryFormDrawer })))
const FactoryDetailDrawer = lazy(() => import('../../drawers/factory-detail-drawer').then(m => ({ default: m.FactoryDetailDrawer })))
const DeleteConfirmDialog = lazy(() => import('../../drawers/delete-confirm-dialog').then(m => ({ default: m.DeleteConfirmDialog })))

// Import auth selectors for admin/role checks
import { selectIsAdmin, selectUserRoleType, isLocationConfigReadOnlyRole } from '@ufc/domains/auth'

// ============================================================================
// Component
// ============================================================================

export function FactoriesSection() {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const isRTL = useIsRTL()
  const isArabic = i18n.language === 'ar' // Keep for content selection (Arabic-specific labels)

  // Redux state
  const factories = useSelector(selectFactoriesItems)
  const stats = useSelector(selectFactoriesStats)
  const loading = useSelector(selectFactoriesLoading)
  const error = useSelector(selectFactoriesError)
  const filter = useSelector(selectFactoriesFilter)
  const locations = useSelector(selectLocations)
  const cities = useSelector(selectCities)
  const citiesSearchResults = useSelector(selectCitiesSearchResults)
  const citiesSearchLoading = useSelector(selectCitiesSearchLoading)
  const citiesHasMore = useSelector(selectCitiesHasMore)
  const citiesLoadMoreLoading = useSelector(selectCitiesLoadMoreLoading)
  const selectedFactory = useSelector(selectSelectedFactory)
  const isAdmin = useSelector(selectIsAdmin)
  const roleType = useSelector(selectUserRoleType)
  const isReadOnly = isLocationConfigReadOnlyRole(roleType)

  // Local state
  const [searchValue, setSearchValue] = useState(filter.search || '')
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [factoryToEdit, setFactoryToEdit] = useState<Factory | null>(null)
  const [factoryToDelete, setFactoryToDelete] = useState<FactoryListItem | null>(null)

  // Fetch factories on mount
  useEffect(() => {
    dispatch(factoriesActions.fetchFactories())
    dispatch(factoriesActions.fetchCountries())
    dispatch(factoriesActions.fetchLocations())
    dispatch(factoriesActions.fetchCities())
  }, [dispatch])

  useEffect(() => {
    setSearchValue(filter.search || '')
  }, [filter.search])

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value)
      dispatch(factoriesActions.searchFactories(value))
    },
    [dispatch]
  )

  const handleCitySearch = useCallback(
    (query: string) => {
      if (query.trim()) {
        dispatch(factoriesActions.searchCitiesInFactory(query))
      } else {
        dispatch(factoriesActions.clearCitiesSearch())
      }
    },
    [dispatch]
  )

  const handleLoadMoreCities = useCallback(() => {
    dispatch(factoriesActions.loadMoreCities())
  }, [dispatch])

  const handleStatusFilter = useCallback(
    (status: string) => {
      dispatch(factoriesActions.filterByStatus(status as 'active' | 'inactive' | 'all'))
    },
    [dispatch]
  )

  const handleAddClick = useCallback(() => {
    setFactoryToEdit(null)
    setIsFormDrawerOpen(true)
  }, [])

  const handleCardSelect = useCallback(
    (factory: FactoryListItem) => {
      // Fetch full factory details for viewing
      dispatch(factoriesActions.fetchFactory(factory.documentId))
      setIsDetailDrawerOpen(true)
    },
    [dispatch]
  )

  const handleCardSettings = useCallback(
    (factory: FactoryListItem) => {
      // Fetch full factory details for editing
      dispatch(factoriesActions.fetchFactory(factory.documentId))
      setIsFormDrawerOpen(true)
    },
    [dispatch]
  )

  // Set factoryToEdit when selectedFactory changes (after fetch)
  useEffect(() => {
    if (selectedFactory && isFormDrawerOpen) {
      setFactoryToEdit(selectedFactory)
    }
  }, [selectedFactory, isFormDrawerOpen])

  const handleEditFromDetail = useCallback((factory: Factory) => {
    setFactoryToEdit(factory)
    setIsDetailDrawerOpen(false)
    setIsFormDrawerOpen(true)
  }, [])

  const handleDeleteFromDetail = useCallback((factory: Factory) => {
    // Convert Factory to FactoryListItem for delete dialog
    const factoryListItem: FactoryListItem = {
      id: factory.id,
      documentId: factory.documentId,
      code: factory.code,
      name: factory.name.en,
      description: factory.description?.en,
      locationName: factory.location?.name,
      address: factory.location?.addressLine1 || '',
      city: factory.location?.city?.name || '',
      country: factory.location?.country?.name || '',
      countryCode: factory.location?.country?.code,
      locationDocumentId: factory.location?.documentId,
      status: factory.status,
      isActive: factory.status === 'active',
      createdAt: factory.createdAt,
      updatedAt: factory.updatedAt,
    }
    setFactoryToDelete(factoryListItem)
    setIsDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (factoryToDelete) {
      dispatch(factoriesActions.deleteFactory(factoryToDelete.documentId))
      setIsDeleteDialogOpen(false)
      setIsDetailDrawerOpen(false)
      setFactoryToDelete(null)
    }
  }, [dispatch, factoryToDelete])

  const handleFormSubmit = useCallback(
    (data: FactoryFormSubmitData) => {
      if (factoryToEdit) {
        // Update existing factory
        const updateRequest: UpdateFactoryRequest = {
          code: data.code,
          name: data.name,
          description: data.description,
          locationId: data.locationId,
          cityIds: data.cityIds,
          status: data.status,
        }
        dispatch(
          factoriesActions.updateFactory({
            documentId: factoryToEdit.documentId,
            data: updateRequest,
          })
        )
      } else {
        // Create new factory
        const createRequest: CreateFactoryRequest = {
          code: data.code,
          name: data.name,
          description: data.description,
          locationId: data.locationId,
          cityIds: data.cityIds,
        }
        dispatch(factoriesActions.createFactory(createRequest))
      }
      setIsFormDrawerOpen(false)
      setFactoryToEdit(null)
    },
    [dispatch, factoryToEdit]
  )

  const handleFormDrawerClose = useCallback((open: boolean) => {
    setIsFormDrawerOpen(open)
    if (!open) {
      setFactoryToEdit(null)
      dispatch(factoriesActions.setSelectedFactory(null))
    }
  }, [dispatch])

  const handleDetailDrawerClose = useCallback((open: boolean) => {
    setIsDetailDrawerOpen(open)
    if (!open) {
      dispatch(factoriesActions.setSelectedFactory(null))
    }
  }, [dispatch])

  const handleRetry = useCallback(() => {
    dispatch(factoriesActions.fetchFactories())
  }, [dispatch])

  // ============================================================================
  // Render
  // ============================================================================

  // Access denied - not an admin (IT has read-only access)
  if (!isAdmin && !isReadOnly) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="text-amber-500 mb-4">
          <ShieldAlert className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('common.accessDenied', 'Access Denied')}
        </h3>
        <p className="text-muted-foreground max-w-md">
          {t(
            'factories.adminOnly',
            'Factory management is restricted to system administrators. Please contact your administrator if you need access.'
          )}
        </p>
      </div>
    )
  }

  // Loading state
  if (loading && factories.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  // Error state
  if (error && factories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="text-destructive mb-4">
          <Building2 className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('common.error')}</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={handleRetry}>{t('common.retry')}</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={cn('flex items-center justify-between flex-wrap gap-4')}>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('factories.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isArabic
              ? `${stats.total} مصنع • ${stats.active} نشط • ${stats.inactive} غير نشط`
              : `${stats.total} factories • ${stats.active} active • ${stats.inactive} inactive`}
          </p>
        </div>
        {!isReadOnly && (
          <Button onClick={handleAddClick}>
            <Plus className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
            {t('factories.addFactory')}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('factories.totalFactories')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('factories.status.active')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('factories.status.inactive')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">{stats.inactive}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className={cn('flex flex-col sm:flex-row gap-4')}>
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('factories.searchPlaceholder')}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            className="ps-10"
          />
        </div>
        <Select value={filter.status || 'all'} onValueChange={handleStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder={t('factories.filterByStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="active">{t('factories.status.active')}</SelectItem>
            <SelectItem value="inactive">{t('factories.status.inactive')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Factories Grid */}
      {factories.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {factories.map((factory) => (
            <FactoryCard
              key={factory.documentId}
              factory={factory}
              onSelect={handleCardSelect}
              onSettings={isReadOnly ? undefined : handleCardSettings}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && factories.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">
            {filter.search || filter.status !== 'all'
              ? (isArabic ? 'لا توجد نتائج' : 'No results found')
              : (isArabic ? 'لا توجد مصانع' : 'No factories found')}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {filter.search || filter.status !== 'all'
              ? (isArabic ? 'جرب تغيير معايير البحث' : 'Try adjusting your search criteria')
              : (isArabic ? 'أضف مصنعاً جديداً للبدء' : 'Add a new factory to get started')}
          </p>
          {!filter.search && filter.status === 'all' && !isReadOnly && (
            <Button className="mt-4" onClick={handleAddClick}>
              <Plus className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
              {t('factories.addFirst')}
            </Button>
          )}
        </div>
      )}

      {/* Detail Drawer */}
      <Suspense fallback={null}>
        <FactoryDetailDrawer
          factory={selectedFactory}
          open={isDetailDrawerOpen}
          onOpenChange={handleDetailDrawerClose}
          onEdit={isReadOnly ? undefined : handleEditFromDetail}
          onDelete={isReadOnly ? undefined : handleDeleteFromDetail}
          isRTL={isRTL}
        />
      </Suspense>

      {/* Form Drawer */}
      <Suspense fallback={null}>
        <FactoryFormDrawer
          open={isFormDrawerOpen}
          onOpenChange={handleFormDrawerClose}
          factory={factoryToEdit}
          locations={locations}
          cities={cities}
          onCitySearch={handleCitySearch}
          citiesSearchResults={citiesSearchResults}
          citiesSearchLoading={citiesSearchLoading}
          citiesHasMore={citiesHasMore}
          citiesLoadMoreLoading={citiesLoadMoreLoading}
          onLoadMoreCities={handleLoadMoreCities}
          onSubmit={handleFormSubmit}
          isLoading={loading}
          isRTL={isRTL}
        />
      </Suspense>

      {/* Delete Confirmation Dialog */}
      {factoryToDelete && (
        <Suspense fallback={null}>
          <DeleteConfirmDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            factoryName={factoryToDelete.name}
            factoryCode={factoryToDelete.code}
            onConfirm={handleDeleteConfirm}
            isLoading={loading}
            isRTL={isRTL}
          />
        </Suspense>
      )}
    </div>
  )
}
